import { useRef } from "react";
import { WeekdaySelector } from "../../../components/product";
import {
  DAY_OF_WEEK_OPTIONS,
  adjustTrainingDaysForSessions,
  areSameTrainingDays,
  getSpacedDefaultTrainingDays,
  normalizeTrainingDays,
  resolvePreferredTrainingDays,
} from "../../onboarding/trainingDayDefaults";
import {
  InlineStepper,
  SectionBlock,
  findFieldError,
  setDraftField,
} from "./shared";

export default function AvailabilitySection({
  draft,
  onChange,
  fieldErrors,
  options,
}) {
  const sessionsValues = options?.sessionsPerWeek || [];
  const durationValues = options?.durationPerSession || [];
  const sessionsValue = draft?.availability?.sessionsPerWeek;
  const durationValue = draft?.availability?.durationPerSession;
  const currentSessions = Number(sessionsValue);
  const currentDuration = Number(durationValue);
  const storedTrainingDays = draft?.availability?.preferredTrainingDays;
  const touchedRef = useRef(null);
  if (touchedRef.current == null) {
    touchedRef.current =
      Array.isArray(storedTrainingDays) &&
      !areSameTrainingDays(
        storedTrainingDays,
        getSpacedDefaultTrainingDays(currentSessions)
      );
  }
  const selectedTrainingDays = touchedRef.current
    ? normalizeTrainingDays(storedTrainingDays)
    : resolvePreferredTrainingDays(storedTrainingDays, currentSessions);
  const hasLegacySessions =
    sessionsValue != null &&
    sessionsValue !== "" &&
    Number.isFinite(currentSessions) && !sessionsValues.includes(currentSessions);
  const hasLegacyDuration =
    durationValue != null &&
    durationValue !== "" &&
    Number.isFinite(currentDuration) && !durationValues.includes(currentDuration);

  function updateSessionsPerWeek(value) {
    onChange({
      ...draft,
      availability: {
        ...draft.availability,
        sessionsPerWeek: value,
        preferredTrainingDays: adjustTrainingDaysForSessions(
          selectedTrainingDays,
          value,
          touchedRef.current
        ),
      },
    });
  }

  function toggleTrainingDay(day) {
    touchedRef.current = true;
    const selected = selectedTrainingDays.includes(day);
    if (!selected && selectedTrainingDays.length >= currentSessions) {
      return;
    }

    onChange({
      ...draft,
      availability: {
        ...draft.availability,
        preferredTrainingDays: normalizeTrainingDays(
          selected
            ? selectedTrainingDays.filter((selectedDay) => selectedDay !== day)
            : [...selectedTrainingDays, day]
        ),
      },
    });
  }

  return (
    <SectionBlock
      title="Availability"
      description="Set the weekly rhythm and session length the plan should respect."
    >
      <div className="space-y-4">
        <InlineStepper
          label="Sessions per week"
          description="How many lifting sessions can you realistically complete?"
          value={draft?.availability?.sessionsPerWeek ?? ""}
          allowedValues={sessionsValues}
          onChange={updateSessionsPerWeek}
        />
        {hasLegacySessions ? (
          <p className="lz-settings-note lz-settings-note--caution">
            Select an available sessions-per-week value before saving.
          </p>
        ) : null}
        {findFieldError(fieldErrors, ["availability.sessionsPerWeek"]) ? (
          <p className="lz-settings-error">
            {findFieldError(fieldErrors, ["availability.sessionsPerWeek"])}
          </p>
        ) : null}

        {currentSessions === 7 ? null : (
          <div className="lz-settings-stepper-block lz-settings-stepper-block--days">
            <div>
              <p className="lz-settings-primary-text">Preferred training days</p>
              <p className="lz-settings-section__description">
                Used for future cycles only. Existing cycles and scheduled sessions will not change.
              </p>
            </div>
            {/* Seven equal-width weekday cells on one row. The selection rule,
                the ordering and the reconciliation with sessionsPerWeek are
                unchanged — only the cells replaced the pill row. */}
            <WeekdaySelector
              options={DAY_OF_WEEK_OPTIONS}
              selectedValues={selectedTrainingDays}
              onToggle={toggleTrainingDay}
              maxSelected={currentSessions}
            />
            <p className="lz-settings-secondary-text">
              {selectedTrainingDays.length}/{currentSessions || 0} selected
            </p>
            {findFieldError(fieldErrors, ["availability.preferredTrainingDays"]) ? (
              <p className="lz-settings-error">
                {findFieldError(fieldErrors, ["availability.preferredTrainingDays"])}
              </p>
            ) : null}
          </div>
        )}

        <InlineStepper
          label="Duration per session"
          description="Choose the session length you can sustain most weeks."
          value={draft?.availability?.durationPerSession ?? ""}
          allowedValues={durationValues}
          onChange={(value) =>
            setDraftField(draft, onChange, ["availability", "durationPerSession"], value)
          }
          suffix="min"
        />
        {hasLegacyDuration ? (
          <p className="lz-settings-note lz-settings-note--caution">
            Select an available session duration before saving.
          </p>
        ) : null}
        {findFieldError(fieldErrors, ["availability.durationPerSession"]) ? (
          <p className="lz-settings-error">
            {findFieldError(fieldErrors, ["availability.durationPerSession"])}
          </p>
        ) : null}
      </div>
    </SectionBlock>
  );
}
